import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Layers } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeaderFrame } from "@/components/layout/page-header-frame";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { usePpp, usePppCandidates, usePppDeliverables } from "@/hooks/usePpp";
import { PppDetailDeliverables } from "@/components/ppp/PppDetailDeliverables";
import { PppLock } from "@/components/ppp/PppLock";
import { PPP_CATEGORY_LABEL, PPP_TYPE_BADGE } from "@/components/ppp/ppp-constants";
import { CATEGORY_BADGE } from "@/components/okr/okrConstants";
import { ContributorStack } from "@/components/okr/ContributorStack";
import { cn } from "@/lib/utils";

const BackLink = () => (
  <Button asChild variant="ghost" className="mb-6 gap-2 px-0">
    <Link to="/ppp">
      <ArrowLeft className="h-4 w-4" />
      Back to PPP
    </Link>
  </Button>
);

const PppDetail = () => {
  const { pid } = useParams<{ pid: string }>();
  const { canViewOkr } = useAuth();
  const { data: ppp, isLoading, isError } = usePpp(pid);
  const deliverablesQuery = usePppDeliverables(pid);
  const candidatesQuery = usePppCandidates(pid);

  if (!pid) {
    return (
      <PageShell>
        <BackLink />
        <div className="rounded-xl border border-dashed p-12 text-center text-sm text-muted-foreground">PPP not found.</div>
      </PageShell>
    );
  }

  if (isLoading) {
    return (
      <PageShell>
        <BackLink />
        <Skeleton className="mb-6 h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </PageShell>
    );
  }

  if (isError || !ppp) {
    return (
      <PageShell>
        <BackLink />
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center text-sm text-destructive">Failed to load this PPP.</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <BackLink />

      <PageHeaderFrame
        icon={Layers}
        title={ppp.name}
        description={ppp.description || "No description yet."}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide", PPP_TYPE_BADGE[ppp.type])}>
            {ppp.type}
          </span>
          {ppp.isConfidential && <PppLock />}
          <ContributorStack contributors={ppp.members} count={ppp.members.length} noun="member" />
          {ppp.category && (
            <span className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide", CATEGORY_BADGE[ppp.category])}>
              {PPP_CATEGORY_LABEL[ppp.category]}
            </span>
          )}
          {ppp.dri ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <UserAvatar src={ppp.dri.avatarUrl} name={ppp.dri.fullName} className="h-5 w-5" fallbackClassName="text-[9px]" />
              {ppp.dri.fullName}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">No DRI</span>
          )}
          <span className="text-xs text-muted-foreground">
            {ppp.deliverableCount} deliverable{ppp.deliverableCount === 1 ? "" : "s"}
          </span>
        </div>

        {ppp.keyResults.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {ppp.keyResults.map((kr) =>
              canViewOkr ? (
                <Link key={kr.pid} to="/okrs">
                  <Badge variant="outline" className="gap-1 font-mono text-[10px] hover:bg-muted" title={kr.name}>
                    {kr.code} · {kr.name}
                  </Badge>
                </Link>
              ) : (
                <Badge key={kr.pid} variant="outline" className="gap-1 font-mono text-[10px]" title={kr.name}>
                  {kr.code} · {kr.name}
                </Badge>
              ),
            )}
          </div>
        )}
      </PageHeaderFrame>

      <PppDetailDeliverables
        deliverables={deliverablesQuery.data?.data ?? []}
        deliverablesLoading={deliverablesQuery.isLoading}
        deliverablesError={deliverablesQuery.isError}
        candidates={candidatesQuery.data?.data ?? []}
        candidatesLoading={candidatesQuery.isLoading}
        candidatesError={candidatesQuery.isError}
      />
    </PageShell>
  );
};

export default PppDetail;
